import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import DateRangePickerComponent from "../../../core/components/common/custom-date-range-picker.component";
import EmptyBox from "../../../assets/icons/empty-box.png";

const containerStatusStyles = {
    "In Port":  '#6e7b8dff',
    "In Transit": '#854D0E',
    "Scheduled":  '#1976d2',
    "In CY/CD": '#068B1F',
    "Out of Port": '#9A3412',
    "Decommissioned":  '#D32F2F',
} 

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <>
        <Typography sx={{fontSize: 16, color: containerStatusStyles[payload[0].payload.status] || "#ccc", bgcolor:'#fff', p: 1}}>
            {payload[0].payload.status}
        </Typography>
        <Box
          style={{
            background: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            minWidth: "220px", // wider for long content
            fontSize: "12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr", // two columns
            columnGap: "12px",
          }}
        >
          {console.log("ssssssssssssssssss", payload)}
          
          {payload.map((entry, index) => (
            
            <Box key={`item-${index}`} style={{ marginBottom: "4px", display:'flex', alignItems:'center' }}>
              
              <strong>{entry.name}:</strong>
              <div>{entry.value}</div>

            </Box>
          ))}
        </Box>
      </>
    );
  }

  return null;
};

export default function ContainerAnalyticsComponent(props) {
  const { data, handleDateRangeChange, startDate, endDate, orgName, getOrgNameByOrgUID } = props;

  const [mappContainerCount, setMappedContainerCount] = React.useState([]);

  const groupedByStatus = {};
  const breakdownByStatus = {};

  mappContainerCount?.forEach(({ name, status, count }) => {
    // For chart stacking
    if (!groupedByStatus[status]) groupedByStatus[status] = { status };
    groupedByStatus[status][name] = count;

    // For accordion breakdown
    if (!breakdownByStatus[status]) {
      breakdownByStatus[status] = {
        status,
        value: 0,
        containers: [],
      };
    }
    breakdownByStatus[status].value += count;
    breakdownByStatus[status].containers.push(`${name} (${count})`);
  });

  const chartData = Object.values(groupedByStatus);
  const aggregatedData = Object.values(breakdownByStatus);

  const containerNames = [...new Set(mappContainerCount?.map((d) => d.name))];

  const colors = [
    "#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#8dd1e1",
    "#a4de6c", "#d0ed57", "#d62728", "#9467bd", "#17becf",
    "#2ca02c", "#ffbb28", "#ff9896", "#c5b0d5", "#1f77b4"
  ];

  

useEffect(() => {
    const orgUIDs = data
      ?.map(item => item.name)
      .filter(name => name !== 'PPA');

    if (orgUIDs?.length > 0) {
      getOrgNameByOrgUID({ org_uids: orgUIDs });
    }
  }, [data]);
  
  
  useEffect(() => {
    if (!data || !orgName) return;
  
    const mapped = data?.map(item => {
      // Keep 'PPA' as is or if name not found
      if (item.name === 'PPA' || !item.name) return item;
  
      return {
        ...item,
        name: orgName[item.name] || item.name,
      };
    });
  
    setMappedContainerCount(mapped);
  }, [data, orgName]);

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: 589}}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Container Component
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <DateRangePickerComponent
            initialStartDate={startDate}
            initialEndDate={endDate}
            ranges={{
              "Today": [moment().startOf("day"), moment().endOf("day")],
              "Last 7 Days": [
                moment().subtract(6, "days").startOf("day"),
                moment().endOf("day"),
              ],
              "This Month": [moment().startOf("month"), moment().endOf("month")],
              "Last Month": [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
              ],
              "Last 90 Days": [
                moment().subtract(89, "days").startOf("day"),
                moment().endOf("day"),
              ],
            }}
            onDateRangeChange={handleDateRangeChange}
          />
        </Box>

        {aggregatedData?.length > 0 ? (
          <Box>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                width={500}
                height={300}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1300 }} />
                {/* <Legend /> */}

                {containerNames.map((container, index) => (
                  <Bar key={container} dataKey={container} stackId="a">
                      {chartData.map((entry, i) => (
                        <Cell
                          key={`cell-${container}-${i}`}
                          fill={containerStatusStyles[entry.status] || "#ccc"}
                        />
                      ))}
                    </Bar>
                  ))}
              </BarChart>
            </ResponsiveContainer>

            {/* Breakdown Accordion */}
            <Accordion sx={{ mt: 4, zIndex: 1200 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">Status Breakdown</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{height: 400, overflowY: "auto", }}>
                <Box sx={{pr: 1 }}>
                  {aggregatedData?.map((entry) => (
                    <Box
                      key={entry.status}
                      sx={{
                        backgroundColor: containerStatusStyles[entry.status] || "#f4f4f5",
                        color: "#374151",
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                          maxHeight: 300, overflowY: "auto", 
                        }}
                      >
                        <Typography variant="body1" fontWeight="bold">
                          {entry.status}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {entry.value}
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 2 }}>
                        {entry.containers.map((container, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{ color: "inherit" }}
                          >
                            • {container}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ):(
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              minHeight: "300px",
              textAlign: "center",
            }}
          >
            <img src={EmptyBox} alt="No Data" width={120} />
            <Typography variant="subtitle1" color="textSecondary" mt={2}>
              No Data Available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
